import "../../../../assets/css/screens/customer/dashboard/mybookings/BookingDetail.css";
import AppNavbar from '../../../../components/customer/dashboard/apphome/appnavbar/AppNavbar';
import { useBookingDetail } from '../../../../context/bookingdetailcontext/BookingDetailContext';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Calendar, Clock, MapPin,
  Phone, MessageSquare, CheckCircle, Star
} from 'lucide-react';

// ── Stepper Info ──
const STEPS = ['Booked','Confirmed','In Progress','Completed'];
const STEP_INDEX = {
  'Confirmed':   1,
  'In Progress': 2,
  'Completed':   3,
  'Cancelled':   0,
};
const STATUS_STYLE = {
  'Confirmed':   { color: '#0491e1', bg: 'rgba(4,145,225,0.10)' },
  'In Progress': { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  'Completed':   { color: '#10b981', bg: 'rgba(16,185,129,0.10)'  },
  'Cancelled':   { color: '#ef4444', bg: 'rgba(239,68,68,0.10)'  },
};

// ── Dummy BOOKINGS (fallback) ──
const BOOKINGS = [
  { id: 1, service: 'AC Repair & Maintenance', provider: 'Ali Hassan', date: '2026-03-20', time: '10:00 AM', address: '123 Main St, Pietersburg', price: '$50', platformFee: '$5', status: 'Confirmed', providerRating: 4.5, providerIcon: '❄️', providerPhone: '+92 300 1234567' },
  { id: 2, service: 'Deep House Cleaning', provider: 'Sarah Miller', date: '2026-03-18', time: '2:00 PM', address: '456 Oak Ave, Pietersburg', price: '$60', platformFee: '$5', status: 'In Progress', providerRating: 4.9, providerIcon: '🧹', providerPhone: '+92 300 9876543' },
];

const BookingDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedBooking, setSelectedBooking } = useBookingDetail();

  // ── Load booking from context or fallback ──
  useEffect(() => {
    if (!selectedBooking) {
      const bookingId = parseInt(id);
      const storedBookings = [
        ...BOOKINGS,
        ...JSON.parse(localStorage.getItem('acceptedBookings') || '[]'),
      ];
      const booking = storedBookings.find(b => b.id === bookingId);
      if (booking) setSelectedBooking(booking);
    }
  }, [id, selectedBooking, setSelectedBooking]);

  const b = selectedBooking;

  if (!b) return (
    <div className="bd-page">
      <div className="bd-header">
        <button className="bd-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={20}/>
        </button>
        <h2>Booking Details</h2>
      </div>
      <p style={{padding:'24px',color:'#484745',opacity:0.6}}>Booking not found.</p>
    </div>
  );

  const st      = STATUS_STYLE[b.status] || STATUS_STYLE['Confirmed'];
  const stepIdx = STEP_INDEX[b.status] ?? 1;
  const total   = parseFloat((b.price || '$0').replace('$','')) +
                  parseFloat((b.platformFee || '$0').replace('$',''));

  return (
    <div className="bd-page">
      <AppNavbar />

      {/* Header */}
      <div className="bd-header">
        <button className="bd-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={20}/>
        </button>
        <h1 className="bd-title">Booking Details</h1>
      </div>

      <div className="bd-content">

        {/* Status Card */}
        <div className="bd-status-card">
          <div className="bd-status-card__top">
            <h2 className="bd-status-card__service">{b.service}</h2>
            <span className="bd-status-pill" style={{ color: st.color, background: st.bg }}>
              {b.status}
            </span>
          </div>

          {/* Stepper */}
          <div className="bd-stepper">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className={'bd-stepper__step' + (i <= stepIdx ? ' bd-stepper__step--done' : '')}>
                  <div className="bd-stepper__dot"/>
                  <span className="bd-stepper__label">{s}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={'bd-stepper__line' + (i < stepIdx ? ' bd-stepper__line--done' : '')}/>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Service Provider */}
        <div className="bd-section">
          <p className="bd-section__title">Service Provider</p>
          <div className="bd-provider">
            <div className="bd-provider__avatar">{b.providerIcon || '👤'}</div>
            <div className="bd-provider__info">
              <div className="bd-provider__name-row">
                <span className="bd-provider__name">{b.provider}</span>
                <CheckCircle size={15} className="bd-provider__verified"/>
              </div>
              <div className="bd-provider__rating">
                <Star size={13} fill="#f59e0b" color="#f59e0b"/>
                <span>{b.providerRating}</span>
              </div>
            </div>
            <div className="bd-provider__actions">
              <a href={`tel:${b.providerPhone}`} className="bd-provider__icon-btn"><Phone size={18}/></a>
              <button className="bd-provider__icon-btn" onClick={() => navigate(`/chat/${b.id}`)}>
                <MessageSquare size={18}/>
              </button>
            </div>
          </div>
        </div>

        {/* Booking Details */}
        <div className="bd-section">
          <p className="bd-section__title">Booking Details</p>
          <div className="bd-detail-list">
            <div className="bd-detail-item">
              <span className="bd-detail-icon"><Calendar size={16}/></span>
              <div>
                <span className="bd-detail-label">Date</span>
                <span className="bd-detail-value">{b.date}</span>
              </div>
            </div>
            <div className="bd-detail-item">
              <span className="bd-detail-icon"><Clock size={16}/></span>
              <div>
                <span className="bd-detail-label">Time</span>
                <span className="bd-detail-value">{b.time}</span>
              </div>
            </div>
            <div className="bd-detail-item">
              <span className="bd-detail-icon"><MapPin size={16}/></span>
              <div>
                <span className="bd-detail-label">Address</span>
                <span className="bd-detail-value">{b.address}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Summary */}
        <div className="bd-section">
          <p className="bd-section__title">Payment Summary</p>
          <div className="bd-payment">
            <div className="bd-payment__row"><span>Service Fee</span><span>{b.price}</span></div>
            <div className="bd-payment__row"><span>Platform Fee</span><span>{b.platformFee}</span></div>
            <div className="bd-payment__divider"/>
            <div className="bd-payment__row bd-payment__row--total">
              <span>Total</span>
              <span className="bd-payment__total">${total}</span>
            </div>
          </div>
        </div>

        {/* Cancel Button */}
        {b.status !== 'Cancelled' && b.status !== 'Completed' && (
          <button className="bd-cancel-btn">Cancel Booking</button>
        )}

      </div>
    </div>
  );
};

export default BookingDetail;